/**
 * Shared form field validators (email / phone).
 */
import { z } from "zod";

import {
  PHONE_INVALID_US_MESSAGE,
  isValidUsPhone,
  normalizeUsPhoneE164,
} from "./phone.ts";

/** Rejects any whitespace anywhere in the string (after trim for empty checks). */
export const emailNoSpaces = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Please enter a valid email address.")
  .max(100)
  .email("Please enter a valid email address.")
  .refine((v) => !/\s/.test(v), "Email cannot contain spaces.");

export function createRequiredPhoneSchema(maxLength: number) {
  return z
    .string()
    .trim()
    .min(1, "Please enter a phone number.")
    .max(maxLength, `Phone number must be ${maxLength} characters or fewer.`)
    .refine(isValidUsPhone, PHONE_INVALID_US_MESSAGE)
    .transform(normalizeUsPhoneE164);
}

export function createOptionalPhoneSchema(maxLength: number) {
  return z
    .string()
    .trim()
    .max(maxLength, `Phone number must be ${maxLength} characters or fewer.`)
    .optional()
    .or(z.literal(""))
    .refine((v) => !v || isValidUsPhone(v), PHONE_INVALID_US_MESSAGE)
    .transform((v) => {
      if (!v) {
        return v;
      }
      return normalizeUsPhoneE164(v);
    });
}

/** Required phone for users/clients — valid US number (E.164), max 32 chars. */
export const requiredPhoneSchema = createRequiredPhoneSchema(32);

/** Optional phone — empty allowed; otherwise valid US (E.164), max 32 chars. */
export const optionalPhoneSchema = createOptionalPhoneSchema(32);

/** Required phone for deal reach-out contact — max 50 chars. */
export const contactPhoneSchema = createRequiredPhoneSchema(50);

/** @deprecated Use `requiredPhoneSchema`. */
export const phoneNoLetters = requiredPhoneSchema;

/** @deprecated Use `optionalPhoneSchema`. */
export const optionalPhoneNoLetters = optionalPhoneSchema;
